/* eslint-disable @next/next/no-img-element */

import { DetailedHTMLProps, ImgHTMLAttributes } from 'react';
import { FragmentImageFragment } from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';

import RemoteImage, {
  getTinyPicturesURL,
  RemoteImageTransformation,
} from './remote-image';

export interface DrupalImageProps
  extends DetailedHTMLProps<
    ImgHTMLAttributes<HTMLImageElement>,
    HTMLImageElement
  > {
  image: FragmentImageFragment;
  /**
   * An array of DrupalImageSize objects that define the viewport width
   * and optimal image pixel width at that viewport width.
   */
  srcsetSizes?: ImageSrcSetSize[];
  /**
   * Optional aspect ratio to apply to the image.  Requires that image data
   * includes width.  Cropping to aspect will use object-fit: cover.
   */
  aspect?: number;

  /**
   * Optionally disable loading animation image background.
   */
  noLoader?: boolean;
}

/**
 * Provides the URL for a Drupal image with a given width.  Tiny pictures image resizing
 * is utilized to provide image resizing, optimization, and global CDN delivery.
 */
export const getDrupalImageURL = (
  image: RemoteImage,
  transformation: RemoteImageTransformation
) => {
  const tinyPicturesURL = image.url
    .replace(
      'https://booking-platform-cms-production.storage.googleapis.com/',
      'https://dreamresorts.tiny.pictures/cms-images/'
    )
    .replace(
      'https://booking-platform-test-cms-qa.storage.googleapis.com/',
      'https://dreamresorts.tiny.pictures/cms-qa-images/'
    );
  return getTinyPicturesURL(tinyPicturesURL, image, transformation);
};

/**
 * DrupalImage is a responsive image component that renders a standard <img>
 * element with a srcset attribute that includes multiple image sizes.  The
 * srcsetSizes prop is an array of DrupalImageSize objects that define the
 * viewport width and image width for each image size.  The last image size
 * will be used as the default image size.
 */
export const DrupalImage: React.FC<DrupalImageProps> = ({
  image,
  srcsetSizes,
  aspect,
  ...props
}) => {
  return (
    <RemoteImage
      image={{ url: image.url, width: image.width, height: image.height }}
      srcsetSizes={srcsetSizes}
      aspect={aspect}
      transform={getDrupalImageURL}
      loading="lazy"
      {...props}
    />
  );
};

export default DrupalImage;
